import { Calendar, Clock } from "lucide-react"

import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Service, ServiceType } from "../../../types/service.type"
import { currencyFormatter } from "../../../utils/formatter"
import {
  getReadablePrimaryButtonStyle,
  resolveOptionalColor,
  resolveThemeColor,
} from "../../../utils/buttonColor"
import { ConfirmationModal } from "../../../components/confirmation-modal"
import { toast, ToastContainer } from "react-toastify"
import { useNavigate } from "react-router"

import Placeholder from '../../../assets/images/placeholder.png'
import { useGetServices } from "../../../hooks/useGetServices"
import { User } from "../../../types/user.type"
import { DataContext, ProjectContext } from "../../../App"
import moment from "moment"

function ServiceSection() {
  const { project } = useContext(ProjectContext)
  const { customer, selectedServices, setSelectedServices } = useContext(DataContext)
  const { services, serviceTypes } = useGetServices()
  const navigate = useNavigate()
  const { t } = useTranslation(['home', 'booking'])
  const [selectedServiceType, setSelectedServiceType] = useState<ServiceType>()
  const [displayedServices, setDisplayedServices] = useState<Service[]>([])
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [serviceToBook, setServiceToBook] = useState<Service | undefined>()

  const themeColor = resolveThemeColor(project?.themeColor)
  const buttonColor = resolveThemeColor(project?.buttonColor)
  const serviceColor = resolveOptionalColor(project?.serviceColor)

  useEffect(() => {
    setSelectedServiceType(serviceTypes[0])
  }, [serviceTypes])

  useEffect(() => {
    setDisplayedServices(services.filter((service: Service) => service.serviceTypeId === selectedServiceType?.serviceTypeId))
  }, [services, selectedServiceType])

  if (services.length === 0) return null

  return (
    <>
      <ToastContainer autoClose={1000} />
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={() => {
          setIsConfirmationModalOpen(false)
          if (serviceToBook) {
            if (selectedServices.some((service) => service.serviceId === serviceToBook.serviceId)) {
              toast.error(t('home:onlyOneService'))
              return
            }
            setSelectedServices([...selectedServices, serviceToBook])
            navigate('/booking')
          }
        }} />
      <section
        className="mx-5 mt-8 mb-8 overflow-hidden rounded-2xl border border-gray-200/90 bg-white text-gray-900 shadow-xl shadow-gray-900/5 sm:mt-10"
        id="services"
      >
        <header
          className="border-b border-gray-100 px-5 py-6 sm:px-8 sm:py-8"
          style={{
            background: `linear-gradient(135deg, ${themeColor}0d 0%, transparent 55%), linear-gradient(to bottom, #fafafa, #ffffff)`,
          }}
        >
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900">{t('home:services')}</h2>
          <p className="mt-1.5 max-w-lg text-sm leading-relaxed text-gray-600">
            {t('home:servicesSubtitle')}
          </p>
        </header>

        <div className="px-5 pb-10 pt-6 sm:px-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{t('home:categories')}</p>
          <div className="-mx-1 mt-3 flex flex-row items-start gap-2 overflow-x-auto pb-2 [-webkit-overflow-scrolling:touch] sm:gap-3">
            {serviceTypes.map((serviceType: ServiceType, index: number) => {
              const isSelected = selectedServiceType?.serviceTypeId === serviceType.serviceTypeId
              return (
                <button
                  type="button"
                  key={index}
                  className={`flex shrink-0 flex-col items-center gap-2 rounded-xl p-2 text-center transition-colors ${
                    isSelected ? 'border-2 bg-gray-50/90' : 'border-2 border-transparent hover:bg-gray-50/80'
                  }`}
                  style={isSelected ? { borderColor: themeColor } : undefined}
                  onClick={() => setSelectedServiceType(serviceType)}
                >
                  <span className="block overflow-hidden rounded-xl bg-gray-100 shadow-sm ring-1 ring-gray-200/80">
                    <img
                      src={serviceType.imageFile?.s3Url || Placeholder}
                      alt={serviceType.type}
                      className="h-20 w-20 object-cover sm:h-24 sm:w-24"
                    />
                  </span>
                  <span
                    className="max-w-[6.5rem] break-words text-center text-xs font-semibold leading-tight sm:text-sm"
                    style={{ color: isSelected ? themeColor : '#374151' }}
                  >
                    {serviceType.type}
                  </span>
                </button>
              )
            })}
          </div>

          <div
            key={selectedServiceType?.serviceTypeId ?? 'empty'}
            className="mt-8 space-y-4"
          >
            {displayedServices.map((service, index) => (
              <article
                key={service.serviceId ?? index}
                className="flex flex-col gap-4 rounded-xl border border-gray-200/90 p-4 text-left shadow-sm transition-shadow hover:shadow-md sm:flex-row sm:items-stretch sm:justify-between sm:gap-6 sm:p-5"
                style={{
                  borderLeftWidth: 4,
                  borderLeftColor: themeColor,
                  ...(serviceColor
                    ? {
                        background: `linear-gradient(135deg, ${serviceColor}12 0%, #ffffff 42%)`,
                      }
                    : { backgroundColor: '#ffffff' }),
                  borderTopColor: '#e5e7eb',
                  borderRightColor: '#e5e7eb',
                  borderBottomColor: '#e5e7eb',
                }}
              >
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-semibold leading-snug" style={{ color: themeColor }}>
                    {service.title}
                  </h3>
                  {service.description ? (
                    <div
                      className="mt-2 w-full min-w-0 max-w-none text-left text-sm leading-relaxed text-gray-600 [overflow-wrap:anywhere] [&_*]:!max-w-full [&_*]:!text-left [&_a]:font-medium [&_a]:underline [&_a]:decoration-gray-400 [&_a]:break-all [&_div]:!w-full [&_img]:max-w-full [&_img]:h-auto [&_p]:!w-full [&_pre]:whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: service.description }}
                    />
                  ) : null}
                  {service.unitValue ? (
                    <p className="mt-3 flex items-center gap-1.5 text-sm text-gray-600">
                      <Clock className="h-4 w-4 shrink-0" style={{ color: themeColor }} aria-hidden />
                      <span>{service.unitValue} {t('home:minutes')}</span>
                    </p>
                  ) : null}
                  {service.validFrom ? (
                    <p className="mt-2 flex flex-wrap items-center gap-x-1 text-xs text-gray-500">
                      <span>{t('home:validFrom')}</span>
                      <Calendar className="inline h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span>{moment(service.validFrom).format('DD MMMM YYYY')}</span>
                    </p>
                  ) : null}
                  {service.validTo ? (
                    <p className="mt-1 flex flex-wrap items-center gap-x-1 text-xs text-gray-500">
                      <span>{t('home:validUntil')}</span>
                      <Calendar className="inline h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span>{moment(service.validTo).format('DD MMMM YYYY')}</span>
                    </p>
                  ) : null}
                </div>

                <div className="flex shrink-0 flex-col gap-3 border-t border-gray-100 pt-4 sm:w-52 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-5">
                  <div className="flex flex-row items-center justify-between gap-3 sm:flex-col sm:items-end sm:justify-start sm:text-right">
                    <ServicePrice
                      price={service.price}
                      customer={customer}
                      customPrice={service.metadata?.price as Record<string, number>}
                      accentColor={themeColor}
                    />
                    <img
                      src={
                        service.imageFile?.s3Url ||
                        selectedServiceType?.imageFile?.s3Url ||
                        Placeholder
                      }
                      alt=""
                      className="h-14 w-14 shrink-0 rounded-lg border border-gray-200 object-cover sm:h-16 sm:w-16"
                    />
                  </div>
                  <button
                    type="button"
                    className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold shadow-sm transition hover:brightness-105 active:brightness-95"
                    style={getReadablePrimaryButtonStyle(buttonColor)}
                    onClick={() => {
                      setServiceToBook(service)
                      setIsConfirmationModalOpen(true)
                    }}
                  >
                    {t('home:bookNow')}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}

function ServicePrice(
  { price, customer, customPrice, accentColor }:
    { price: number, customer?: User, customPrice?: Record<string, number>, accentColor: string }
) {
  const isCustom = customer && customPrice && customPrice[customer?.type]

  return (
    <div className="flex flex-col items-end gap-0.5">
      <div
        className="tabular-nums"
        style={
          isCustom
            ? { textDecoration: 'line-through', fontSize: '0.8125rem', opacity: 0.75, color: accentColor }
            : { color: accentColor }
        }
      >
        {currencyFormatter(price)}
      </div>
      {isCustom && customPrice && customer?.type ? (
        <div className="text-lg font-bold tabular-nums" style={{ color: accentColor }}>
          {currencyFormatter(customPrice[customer.type])}
        </div>
      ) : null}
    </div>
  )
}

export default ServiceSection
